import { createSignal, createEffect } from 'solid-js';
import { createStore } from 'solid-js/store';
import { apiService } from '@/services/api';

// Chat message interface
export interface ChatMessage {
  id: number;
  sender_type: 'team' | 'operator';
  content: string;
  image_url: string | null;
  is_read: boolean;
  created_at: string;
}

// Chat state store
const [chatState, setChatState] = createStore<{
  messages: ChatMessage[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  isOpen: boolean;
}>({
  messages: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  isOpen: false,
});

// Sending state
const [isSending, setIsSending] = createSignal(false);

// Sound notification enabled
const [soundEnabled, setSoundEnabled] = createSignal(true);

// Chat notification sound using Web Audio API
let audioContext: AudioContext | null = null;
let audioInitialized = false;

// Initialize audio context (needs user interaction first)
const initAudio = () => {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioInitialized = true;
      console.log('Audio context initialized for chat notifications');
    } catch (err) {
      console.log('Web Audio API not supported');
    }
  }
  return audioContext;
};

// Initialize audio on first user interaction (required by browser policy)
if (typeof window !== 'undefined') {
  const initOnInteraction = () => {
    if (!audioInitialized) {
      initAudio();
      // Resume if suspended
      if (audioContext && audioContext.state === 'suspended') {
        audioContext.resume();
      }
    }
  };

  // Listen for any user interaction to enable audio
  document.addEventListener('click', initOnInteraction, { once: false });
  document.addEventListener('touchstart', initOnInteraction, { once: false });
  document.addEventListener('keydown', initOnInteraction, { once: false });
}

// Play notification sound using Web Audio API - LOUD and LONG
const playNotificationSound = () => {
  if (!soundEnabled()) return;

  const ctx = initAudio();
  if (!ctx) return;

  try {
    // Resume audio context if suspended (browser policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;

    // Beep 1
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = 'square';
    osc1.frequency.setValueAtTime(880, now); // A5
    gain1.gain.setValueAtTime(0.8, now);
    gain1.gain.setValueAtTime(0, now + 0.3);
    osc1.start(now);
    osc1.stop(now + 0.3);

    // Beep 2
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = 'square';
    osc2.frequency.setValueAtTime(1100, now + 0.4); // C#6
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.setValueAtTime(0.8, now + 0.4);
    gain2.gain.setValueAtTime(0, now + 0.7);
    osc2.start(now + 0.4);
    osc2.stop(now + 0.7);

    // Beep 3 - longer
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    osc3.type = 'square';
    osc3.frequency.setValueAtTime(1320, now + 0.8); // E6
    gain3.gain.setValueAtTime(0, now);
    gain3.gain.setValueAtTime(0.8, now + 0.8);
    gain3.gain.exponentialRampToValueAtTime(0.01, now + 1.5);
    osc3.start(now + 0.8);
    osc3.stop(now + 1.5);

    console.log('Chat notification played (LOUD)');
  } catch (err) {
    console.log('Could not play notification sound:', err);
  }
};

// Track last known message ID for detecting new messages via polling
let lastKnownMessageId = 0;

// Load chat messages (also used for polling)
const loadMessages = async () => {
  // Only show loading on first load
  const isFirstLoad = chatState.messages.length === 0;
  if (isFirstLoad) {
    setChatState('isLoading', true);
  }
  setChatState('error', null);

  try {
    const response = await apiService.getChatMessages();
    if (response.success) {
      const newMessages = response.messages || [];

      // Check for new operator messages (for sound notification)
      if (lastKnownMessageId > 0) {
        const newOperatorMessages = newMessages.filter(
          (m: ChatMessage) => m.id > lastKnownMessageId && m.sender_type === 'operator'
        );

        if (newOperatorMessages.length > 0) {
          console.log(`New operator messages detected via polling: ${newOperatorMessages.length}`);
          playNotificationSound();

          // Show browser notification for the latest message
          if (!chatState.isOpen) {
            showBrowserNotification(newOperatorMessages[newOperatorMessages.length - 1]);
          }
        }
      }

      // Update last known message ID
      if (newMessages.length > 0) {
        lastKnownMessageId = Math.max(...newMessages.map((m: ChatMessage) => m.id));
      }

      setChatState('messages', newMessages);

      // Calculate unread count from operator messages
      const unread = newMessages.filter(
        (m: ChatMessage) => m.sender_type === 'operator' && !m.is_read
      ).length;
      setChatState('unreadCount', unread);
    }
  } catch (error: any) {
    setChatState('error', error.message || 'Failed to load messages');
    console.error('Error loading chat messages:', error);
  } finally {
    setChatState('isLoading', false);
  }
};

// Send a message
const sendMessage = async (content: string, image?: File): Promise<boolean> => {
  if ((!content.trim() && !image) || isSending()) {
    return false;
  }

  setIsSending(true);

  try {
    const response = await apiService.sendChatMessage(content, image);
    if (response.success && response.message) {
      // Add message to local state
      setChatState('messages', [...chatState.messages, response.message]);
      return true;
    }
    return false;
  } catch (error: any) {
    console.error('Error sending message:', error);
    return false;
  } finally {
    setIsSending(false);
  }
};

// Mark messages as read
const markAsRead = async () => {
  // Always call API to mark messages as read (don't rely on local unreadCount state)
  try {
    console.log('Marking chat messages as read...');
    const result = await apiService.markChatMessagesRead();
    console.log('Mark as read result:', result);

    // Update local state - mark all operator messages as read
    setChatState('messages', chatState.messages.map(msg =>
      msg.sender_type === 'operator' ? { ...msg, is_read: true } : msg
    ));
    setChatState('unreadCount', 0);
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

// Show browser notification if permitted
const showBrowserNotification = (message: ChatMessage) => {
  // Check if notifications are supported and permitted
  if (!('Notification' in window)) return;

  if (Notification.permission === 'granted') {
    try {
      new Notification('Nowa wiadomosc od organizatora', {
        body: message.content || '[Zdjecie]',
        icon: '/favicon.ico',
        tag: 'chat-message',
        requireInteraction: false
      });
    } catch (e) {
      console.log('Browser notification failed:', e);
    }
  } else if (Notification.permission !== 'denied') {
    // Request permission for future notifications
    Notification.requestPermission();
  }
};

// Handle new message from WebSocket
const handleNewMessage = (message: ChatMessage) => {
  // Check if message already exists
  const exists = chatState.messages.some(m => m.id === message.id);
  if (exists) return;

  // Add message to state
  setChatState('messages', [...chatState.messages, message]);

  // If from operator and chat is not open, increment unread and play sound
  if (message.sender_type === 'operator') {
    if (!chatState.isOpen) {
      setChatState('unreadCount', chatState.unreadCount + 1);
      // Try to show browser notification
      showBrowserNotification(message);
    }
    // Always play sound for operator messages
    playNotificationSound();

    // Log for debugging
    console.log('New operator message received, playing notification sound');
  }
};

// Handle unread count update from WebSocket
const handleUnreadCount = (count: number) => {
  setChatState('unreadCount', count);
};

// Set chat panel open/closed
const setChatOpen = (isOpen: boolean) => {
  setChatState('isOpen', isOpen);

  // Mark messages as read when opening
  if (isOpen && chatState.unreadCount > 0) {
    markAsRead();
  }
};

// Load unread count on init
const loadUnreadCount = async () => {
  try {
    const response = await apiService.getChatUnreadCount();
    setChatState('unreadCount', response.unread_count || 0);
  } catch (error) {
    console.error('Error loading unread count:', error);
  }
};

// Clear chat (called when game ends)
const clearChat = () => {
  setChatState({
    messages: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    isOpen: false,
  });
};

// Export store and actions
export const chatStore = {
  // State
  get state() {
    return chatState;
  },
  get isSending() {
    return isSending;
  },
  get soundEnabled() {
    return soundEnabled;
  },

  // Actions
  loadMessages,
  sendMessage,
  markAsRead,
  handleNewMessage,
  handleUnreadCount,
  setChatOpen,
  loadUnreadCount,
  clearChat,
  setSoundEnabled,
  playNotificationSound,
};

export default chatStore;
